/**
 * Место загрузки документов клиента — общее для блоков «Кредиторы»,
 * «Имущество», «Доходы» и перечня приложений.
 *
 * Раньше это была ссылка в строку текста, и юрист её не замечал.
 * Теперь — выделенная область с кнопкой; файлы можно и перетащить в неё
 * из папки клиента. Принимаются только PDF: остальное разбор не читает,
 * и лучше сказать об этом сразу, чем получить отказ сервера по каждому файлу.
 */

import { useRef, useState, type DragEvent } from 'react';

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

export function DocumentUpload({
  label,
  busyLabel,
  hint,
  busy,
  disabled,
  onFiles,
}: {
  label: string;
  busyLabel: string;
  hint: string;
  busy: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const off = disabled || busy;

  const take = (files: File[]) => {
    setRejected(files.filter((f) => !isPdf(f)).map((f) => f.name));
    const pdfs = files.filter(isPdf);
    if (pdfs.length) onFiles(pdfs);
  };

  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (!off) take([...e.dataTransfer.files]);
  };

  return (
    <div
      className={`document-upload${over && !off ? ' over' : ''}${off ? ' off' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!off) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <input
        ref={input}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        hidden
        onChange={(e) => {
          take([...(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
      <button type="button" className="primary" disabled={off} onClick={() => input.current?.click()}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
        </svg>
        {busy ? busyLabel : label}
      </button>
      <div className="document-upload-text">
        <span>{busy ? 'Разбираю, это может занять несколько секунд' : 'или перетащите PDF сюда'}</span>
        <span className="hint">{hint}</span>
      </div>
      {rejected.length > 0 && <p className="error-text document-upload-rejected">Не PDF, пропущено: {rejected.join(', ')}</p>}
    </div>
  );
}
